<template>
  <label
    :for="id"
    :class="fieldClasses">

    <input
      type="radio"
      :id="id"
      :name="name"
      :value="value"
      :class="inputClasses"
      :disabled="disabled"
      @change="onChange"
      :checked="state"
      v-bind="$attrs"
      v-on="listeners">

    <slot name="box">
      <span :class="boxClasses.box">
        <span
          class="min-w-full min-h-full w-full h-full"
          :class="boxClasses.circle"/>
      </span>
    </slot>

    <div
      v-if="label || $slots.label"
      :class="labelClasses"
      :style="labelStyle">
      <slot name="label">
        {{ label }}
      </slot>
      <span
        v-if="optional && !disabled"
        :class="optionalClass"
        :style="optionalStyle">
        <slot name="optional">{{ optionalMessage }}</slot>
      </span>

      <slot name="extra" />
    </div>
  </label>
</template>

<script>
import _ from 'lodash'

import FormControl from './mixins/FormControl'
import CheckControl from './mixins/CheckControl'

export default {
  mixins: [FormControl, CheckControl],

  props: {
    name: {
      type: String,
      required: true
    },
    boxClass: {
      type: Object,
      default: function () {
        return {
          box: 'overflow-hidden m-0 p-0 bg-white',
          circle: ''
        }
      }
    }
  },

  watch: {
    checked (newValue) {
      if (newValue !== this.state) {
        this.toggle()
      }
    },

    state (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.$emit('change', { state: this.state, value: this.modelValue })
      }
    }
  },

  computed: {
    state () {
      if (this.modelValue === undefined) {
        return this.checked
      }

      return _.isEqual(this.modelValue, this.value)
    },

    fieldClasses () {
      return ['relative', this.fieldClass, {
        [this.disabledClass.field]: this.disabled
      }]
    },

    labelClasses () {
      return [this.labelClass, 'relative', 'inline-block', 'ml-2', {
        [this.disabledClass.label]: this.disabled
      }]
    },

    inputClasses () {
      return ['absolute', 'appearance-none', 'opacity-0', this.$style['box-size'], {
        'cursor-pointer': !this.disabled,
        [this.disabledClass.input]: this.disabled
      }]
    },

    boxClasses () {
      return {
        box: ['inline-block', 'rounded-full', 'border', this.$style['box'], this.$style['box-size'], this.boxClass.box, {
          [this.$style['checked']]: this.state,
          'cursor-pointer': !this.disabled,
          [this.disabledClass.input]: this.disabled
        }],
        circle: ['block', 'rounded-full', this.$style['circle'], this.boxClass.circle]
      }
    }
  },

  methods: {
    toggle () {
      this.$emit('input', this.state ? '' : this.value)
      this.$emit('change', { value: this.value, state: this.state })
    }
  }
}
</script>

<style lang="scss" module>
.box-size {
  width: 1em;
  height: 1em;
}

.box {
  vertical-align: -.2em;
  user-select: none;
  transition: background .1s ease;

  .circle {
    opacity: 0;
    transform: scale(0);
    transition: transform .15s ease-in;
    will-change: transform;
  }

  &.checked > .circle {
    opacity: 1;
    transform: scale(.5);
  }
}
</style>
